/**
 * Audio-bus media-error decision core, extracted verbatim from use-playback's
 * onError handler — the audio twin of screen-video-policy's
 * recordScreenFailure. The hook keeps every side effect (telemetry, resync
 * request, halt-retry timer, advance); this module owns only the decision.
 */

import {
  classifyMediaError,
  type DeadItemRegistry,
  type FailureBudget,
  type MediaFailureKind,
} from "./playback-failure-policy.ts";

export type AudioErrorDecision =
  /**
   * The attention tone is decoration, never content: a dead tone URL must
   * advance straight to the announcement without spending a failure-budget
   * slot or emitting playback_failed (a resync is still requested so the
   * next takeover gets a fresh hydrated URL).
   */
  | { kind: "tone-skip" }
  /**
   * The item has failed too many times to be worth another attempt (usually its
   * object is missing from storage). Skip it WITHOUT spending failure budget:
   * counting it is what made one dead asset halt the whole channel and then
   * retry it forever behind a growing backoff.
   */
  | { kind: "skip-dead"; classification: MediaFailureKind }
  /** Single bad item: skip ahead — other queue items may be valid. */
  | {
      kind: "advance";
      classification: MediaFailureKind;
      consecutiveFailures: number;
    }
  /**
   * Every plausible candidate failed inside the window: stop the skip-storm,
   * surface an error status, and retry after a backoff (resync + advance).
   */
  | {
      kind: "halt";
      classification: MediaFailureKind;
      consecutiveFailures: number;
    };

/**
 * Record an audio-bus media failure and decide recovery. `candidateCount` is
 * everything advancing could still reach (interrupt queue + music rotation).
 * An `expired-url` classification means skipping won't heal the failure — the
 * caller should request a resync for fresh presigned URLs in addition to
 * whatever this decision says.
 */
export function recordAudioFailure(args: {
  budget: FailureBudget;
  isToneEntry: boolean;
  mediaErrorCode: number | null | undefined;
  urlExpiresAt?: Date | string | number | null;
  candidateCount: number;
  /** Identifies the item across resyncs, so repeat failures are recognisable. */
  itemId?: string | null;
  /** Known HTTP status, when the transport saw one (404/410 ⇒ permanent). */
  httpStatus?: number | null;
  /** Cross-attempt memory of items that can never play. */
  deadItems?: DeadItemRegistry;
  now?: number;
}): AudioErrorDecision {
  if (args.isToneEntry) return { kind: "tone-skip" };
  const classification = classifyMediaError({
    mediaErrorCode: args.mediaErrorCode,
    urlExpiresAt: args.urlExpiresAt,
    httpStatus: args.httpStatus,
    now: args.now,
  });

  // A resync cannot conjure an object that is not there, so "missing" is proof
  // rather than evidence and retiring the item immediately is correct.
  const dead =
    args.deadItems?.recordFailure(args.itemId, classification === "missing") ??
    false;
  if (dead) return { kind: "skip-dead", classification };

  const consecutiveFailures = args.budget.record(args.now);
  return {
    kind: args.budget.tripped(args.candidateCount) ? "halt" : "advance",
    classification,
    consecutiveFailures,
  };
}
